import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../../components/modern/PageLayout/PageLayout';
import ActionButton from '../../../components/modern/ActionButton/ActionButton';
import { MultiLanguageTextInput } from '../../../components/modern';
import { adminApi } from '../../../utils/api';
import './EditCity.scss';

interface Country {
  id: number;
  name: string;
}

interface City {
  id: number;
  name: string;
  countryId: number;
  localDeliveryFee: number;
}

const EditCity: React.FC = () => {
  const { t } = useTranslation(['cities', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState('');
  const [countryId, setCountryId] = useState<number>(0);
  const [localDeliveryFee, setLocalDeliveryFee] = useState<number>(0);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCity();
      fetchCountries();
    }
  }, [id]);

  const fetchCity = async () => {
    try {
      const response = await adminApi.call(`/city/${id}`, 'GET');

      if (!response.success) {
        setError(response.error || t('messages.fetchError'));
        return;
      }

      const city: City = response.data;
      setName(city.name);
      setCountryId(city.countryId);
      setLocalDeliveryFee(city.localDeliveryFee || 0);
    } catch (error) {
      console.error('Error fetching city:', error);
      setError(t('messages.fetchError'));
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await adminApi.call('/country?page=1&pageSize=1000', 'GET');

      if (!response.success) {
        setError(response.error || t('messages.fetchCountriesError'));
        return;
      }

      setCountries(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setError(t('messages.fetchCountriesError'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!name.trim()) {
      setError(t('validation.nameRequired'));
      setLoading(false);
      return;
    }

    if (!countryId) {
      setError(t('validation.countryRequired'));
      setLoading(false);
      return;
    }

    try {
      const response = await adminApi.call('/city', 'PUT', {
        id: Number(id),
        name: name.trim(),
        countryId: countryId,
        localDeliveryFee: localDeliveryFee,
      });

      if (!response.success) {
        setError(response.error || t('messages.updateError'));
        return;
      }

      navigate('/cities');
    } catch (error) {
      console.error('Error updating city:', error);
      setError(t('messages.updateError'));
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <PageLayout 
        title={t('edit.title')} 
        breadcrumbs={[
          { label: t('title'), path: '/cities' },
          { label: t('edit.title') }
        ]}
      >
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common:loading')}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title={t('edit.title')} 
      breadcrumbs={[
        { label: t('title'), path: '/cities' },
        { label: t('edit.title') }
      ]}
    >
      <div className="edit-city">
        <div className="card">
          <div className="card-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <MultiLanguageTextInput
                    label={t('fields.name')}
                    value={name}
                    onChange={(value) => {
                      setName(value);
                      setError(null);
                    }}
                    placeholder={t('placeholders.enterName')}
                    error={error}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t('fields.country')}</label>
                    <select
                      className="form-control"
                      value={countryId}
                      onChange={(e) => setCountryId(Number(e.target.value))}
                      required
                    >
                      <option value="">{t('placeholders.selectCountry')}</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t('fields.localDeliveryFee')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      value={localDeliveryFee}
                      onChange={(e) => setLocalDeliveryFee(Number(e.target.value))}
                      placeholder={t('placeholders.enterLocalDeliveryFee')}
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <ActionButton
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={loading}
                >
                  {t('actions.update')}
                </ActionButton>
                <ActionButton
                  type="button"
                  variant="outline-secondary"
                  onClick={() => navigate('/cities')}
                  disabled={loading}
                >
                  {t('cities:actions.cancel')}
                </ActionButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default EditCity;
